import { memo, useMemo } from "react";

import { useTranslate } from "@similarweb/i18n-provider";

import { percentageFilter } from "app/filters/ngFilters";
import { addPercentPoint } from "app/pages/activation/helpers/format";

import * as SC from "./styles";
import { DECLINING, EMPTY_SUB_MESSAGE, GROWING, STAGNATING } from "./texts";
import type { TopicTrafficChangeState, ITopicTrafficChangeWidgetProps } from "./types";
import { getColors, getState } from "./utils";

export const TopicTrafficChangeWidget = memo(function TopicTrafficChangeWidget(props: ITopicTrafficChangeWidgetProps) {
    const { topicName, change, stagnationFrom = 0, stagnationTo = 0, labels, className } = props;
    const state = useMemo(() => getState(change, stagnationFrom, stagnationTo), [change, stagnationFrom, stagnationTo]);
    const colors = useMemo(() => getColors(state), [state]);

    const { translate } = useTranslate();

    const stateLabel = useMemo(() => {
        const defaults: Record<Exclude<TopicTrafficChangeState, "empty">, string> = {
            growing: GROWING,
            declining: DECLINING,
            stagnating: STAGNATING,
        };

        if (state === "empty") {
            return EMPTY_SUB_MESSAGE;
        }

        return labels?.[state] ?? defaults[state];
    }, [labels, state]);

    const valueText = useMemo(() => {
        if (state === "empty") {
            return "—";
        }

        return addPercentPoint(percentageFilter()(change, 2));
    }, [state, change]);

    return (
        <SC.Container
            className={className}
            $bg={colors.bg}
        >
            <SC.Center>
                <SC.Block
                    $bg={colors.blockBg}
                    $text={colors.text}
                >
                    <SC.Title>
                        {translate(stateLabel)} <SC.TopicName>"{topicName}"</SC.TopicName>
                    </SC.Title>
                </SC.Block>

                <SC.Block
                    $bg={colors.blockBg}
                    $text={colors.text}
                >
                    <SC.Value>{valueText}</SC.Value>
                </SC.Block>
            </SC.Center>
        </SC.Container>
    );
});
